/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/JobQaAddQuotes.tsx
 * FILE_ID: JOBHUB-QA-ADD-QUOTES-001
 * PURPOSE: Keep an add-quote door on the existing-job QA setup card.
 * WHY: After the first quote the card was only a picker, so upload vanished.
 */
import { useEffect, useRef, useState } from 'react'
import { HardDrive, Loader2, Search, Upload } from 'lucide-react'
import QDriveFilePicker from '@quotech/quote-builder/components/QDriveFilePicker'
import { cn } from '@quotech/tenant-shared/lib/utils'
import type { QaJobFile } from '../../qa/qaApi'
import { quoteFileRejection } from './quoteFileRejection'

const ACCEPT = [
  '.xlsx', '.xlsm', '.xls', '.csv', '.tsv', '.tab', '.ods',
  '.pdf', '.docx', '.txt',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'text/tab-separated-values',
].join(',')

export type QuoteAttachDestination = 'this-job' | 'new-job'

export type BuilderQuoteHit = {
  id: string
  quote_number: string
  status: string
  revision: number
  is_latest_revision: boolean
  job_number: string | null
}

export function JobQaAddQuotes({
  destination,
  error,
  listQDriveFiles,
  onDestinationChange,
  onSearchCrm,
  onAttachBuilder,
  onAttachFile,
  onAttachQDrive,
  loadRevisions,
  onHideBuilder,
  pending,
  canCreateJob = true,
}: {
  destination: QuoteAttachDestination
  error: string | null
  listQDriveFiles: (query?: string) => Promise<QaJobFile[]>
  onDestinationChange: (value: QuoteAttachDestination) => void
  onSearchCrm: (search: string) => Promise<BuilderQuoteHit[]>
  onAttachBuilder: (ids: string[], includeFamily: boolean) => Promise<void>
  onAttachFile: (file: File) => Promise<void>
  onAttachQDrive: (fileRecordId: string) => Promise<void>
  loadRevisions?: (builderQuoteId: string) => Promise<Array<{
    id: string
    quote_number: string
    revision: number
    status: string
  }>>
  onHideBuilder?: (builderQuoteId: string) => Promise<void>
  pending: boolean
  canCreateJob?: boolean
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [qdriveOpen, setQdriveOpen] = useState(false)
  const [crmOpen, setCrmOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [hits, setHits] = useState<BuilderQuoteHit[]>([])
  const [pickedIds, setPickedIds] = useState<string[]>([])
  const [revisionsByHit, setRevisionsByHit] = useState<Record<string, Array<{
    id: string
    quote_number: string
    revision: number
    status: string
  }>>>({})
  const [checkedRevisionIds, setCheckedRevisionIds] = useState<string[]>([])
  const [searching, setSearching] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const [results, setResults] = useState<string[]>([])

  useEffect(() => {
    if (!canCreateJob && destination === 'new-job') onDestinationChange('this-job')
  }, [canCreateJob, destination, onDestinationChange])

  useEffect(() => {
    const trimmed = search.trim()
    if (!crmOpen || trimmed.length < 1) {
      setHits([])
      return
    }
    const handle = window.setTimeout(() => {
      setSearching(true)
      onSearchCrm(trimmed)
        .then(setHits)
        .catch((err: unknown) => {
          setLocalError(err instanceof Error ? err.message : 'Could not search quotes.')
        })
        .finally(() => setSearching(false))
    }, 250)
    return () => window.clearTimeout(handle)
  }, [crmOpen, onSearchCrm, search])

  const run = async (work: () => Promise<void>) => {
    setLocalError(null)
    try {
      await work()
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Could not add that quote.')
    }
  }

  return (
    <div className="mt-2 mb-3 space-y-2">
      <div className="flex flex-wrap gap-2">
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? [])
            event.target.value = ''
            if (files.length === 0) return
            void (async () => {
              const lines: string[] = []
              for (const file of files) {
                const rejected = quoteFileRejection(file.name)
                if (rejected) {
                  lines.push(rejected)
                  continue
                }
                try {
                  await onAttachFile(file)
                  lines.push(`${file.name}: added`)
                } catch (err) {
                  lines.push(`${file.name}: ${err instanceof Error ? err.message : 'could not import'}`)
                }
              }
              setResults(lines)
            })()
          }}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={pending}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50"
        >
          {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Upload className="h-3.5 w-3.5" aria-hidden />}
          Add from device
        </button>
        <button
          type="button"
          onClick={() => setQdriveOpen(true)}
          disabled={pending}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50"
        >
          <HardDrive className="h-3.5 w-3.5" aria-hidden />
          From QDrive
        </button>
        <button
          type="button"
          onClick={() => setCrmOpen((open) => !open)}
          disabled={pending}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50"
        >
          <Search className="h-3.5 w-3.5" aria-hidden />
          From CRM
        </button>
      </div>

      {crmOpen && (
        <div className="space-y-2 rounded-lg border border-border p-2">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search quote number"
            className="w-full min-w-0 rounded-md border border-border bg-background px-2 py-1.5 text-xs"
            aria-label="Search CRM quotes"
          />
          {searching && <p className="text-xs text-muted-foreground">Searching…</p>}
          <ul className="max-h-40 space-y-1 overflow-y-auto">
            {hits.map((hit) => {
              const checked = pickedIds.includes(hit.id)
              return (
                <li key={hit.id}>
                  <label className={cn(
                    'flex items-center gap-2 rounded-md px-2 py-1 text-xs',
                    checked ? 'bg-primary/5' : 'hover:bg-accent',
                  )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = pickedIds.includes(hit.id)
                          ? pickedIds.filter((id) => id !== hit.id)
                          : [...pickedIds, hit.id]
                        setPickedIds(next)
                        if (!pickedIds.includes(hit.id) && loadRevisions) {
                          void loadRevisions(hit.id).then((rows) => {
                            const list = rows.length ? rows : [{
                              id: hit.id,
                              quote_number: hit.quote_number,
                              revision: hit.revision,
                              status: hit.status,
                            }]
                            setRevisionsByHit((current) => ({ ...current, [hit.id]: list }))
                            setCheckedRevisionIds((current) => [
                              ...current,
                              ...list.map((row) => row.id).filter((id) => !current.includes(id)),
                            ])
                          })
                        }
                      }}
                    />
                    <span className="min-w-0 truncate font-medium">{hit.quote_number}</span>
                    <span className="shrink-0 text-muted-foreground">{hit.status}</span>
                  </label>
                  {checked && (revisionsByHit[hit.id] ?? []).map((revision) => (
                    <label key={revision.id} className="ml-6 flex items-center gap-2 px-2 py-1 text-xs">
                      <input
                        type="checkbox"
                        checked={checkedRevisionIds.includes(revision.id)}
                        onChange={() => setCheckedRevisionIds((current) => (
                          current.includes(revision.id)
                            ? current.filter((id) => id !== revision.id)
                            : [...current, revision.id]
                        ))}
                      />
                      <span className="min-w-0 truncate">{revision.quote_number}</span>
                      <span className="shrink-0 text-muted-foreground">R{revision.revision}</span>
                      <span className="shrink-0 text-muted-foreground">{revision.status}</span>
                    </label>
                  ))}
                </li>
              )
            })}
          </ul>
          <button
            type="button"
            disabled={pending || checkedRevisionIds.length === 0}
            onClick={() => void (async () => {
              const names = checkedRevisionIds
              try {
                for (const id of checkedRevisionIds) {
                  await onAttachBuilder([id], false)
                }
                const shown = Object.values(revisionsByHit).flat()
                for (const revision of shown) {
                  if (!checkedRevisionIds.includes(revision.id)) {
                    await onHideBuilder?.(revision.id)
                  }
                }
                setResults(names.map((name) => `${name}: added`))
                setPickedIds([])
                setCheckedRevisionIds([])
                setCrmOpen(false)
              } catch (err) {
                const message = err instanceof Error ? err.message : 'could not import'
                setResults(names.map((name) => `${name}: ${message}`))
              }
            })()}
            className="rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
          >
            Add selected to this job
          </button>
        </div>
      )}

      {(error || localError) && (
        <p className="text-xs text-destructive">{error || localError}</p>
      )}
      {results.length > 0 && (
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {results.map((line, index) => <li key={`${line}-${index}`}>{line}</li>)}
        </ul>
      )}

      <QDriveFilePicker
        open={qdriveOpen}
        title="Choose a quote from QDrive"
        description="Pick one or more quotes already filed in QDrive. They attach to this job and do not build QA."
        multiSelect
        onClose={() => setQdriveOpen(false)}
        loadFiles={listQDriveFiles}
        onSelect={async (file) => {
          await run(() => onAttachQDrive(file.id))
          setQdriveOpen(false)
        }}
        onSelectMany={async (files) => {
          const lines: string[] = []
          for (const file of files) {
            const rejected = quoteFileRejection(file.filename)
            if (rejected) {
              lines.push(rejected)
              continue
            }
            try {
              await onAttachQDrive(file.id)
              lines.push(`${file.filename}: added`)
            } catch (err) {
              lines.push(`${file.filename}: ${err instanceof Error ? err.message : 'could not import'}`)
            }
          }
          setResults(lines)
          setQdriveOpen(false)
        }}
      />
    </div>
  )
}
